/**
 * WGMinutes Component - Working Group Meeting Minutes Display - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Interactive display component for working group meeting minutes with year navigation,
 * card/list view toggles, and PDF modal integration. Features intelligent tooltip
 * positioning, text cleaning utilities, and responsive year-based filtering with
 * chronological sorting by month and date.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of meeting minutes data objects
 * @param {string} props.data[].id - Unique identifier for the meeting
 * @param {string} props.data[].title - Meeting title
 * @param {string} props.data[].summary - Meeting summary
 * @param {string} props.data[].fileName - PDF file name
 * @param {number} props.data[].year - Meeting year
 * @param {string} props.data[].month - Meeting month
 * @param {string} props.data[].publishDate - Optional publish date
 * @returns {JSX.Element} The WGMinutes component
 * 
 * @example
 * <WGMinutes data={meetingMinutesArray} />
 */

import React, { useState, useMemo } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './WGMinutes.css';
import PDFModal from '../../modals/PDFModal';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import './WGMinutesCards.css';

function WGMinutes({ data }) {
  const [modals, setModals] = useState([]);
  const [selectedYear, setSelectedYear] = useState(() => {
    const years = [...new Set(data.map(item => item.year))].sort((a, b) => b - a);
    return years[0] || new Date().getFullYear();
  });

  // Group minutes by year and month for organized display
  const groupedMinutes = useMemo(() => {
    /**
     * Groups meeting minutes by year and month, then sorts chronologically
     * Handles optional publishDate fallback for month calculation
     * 
     * @returns {Object} Nested object structure: year -> month -> array of meetings
     */
    const groups = data.reduce((acc, item) => {
      const year = item.year;
      const month = new Date(item.publishDate || `${item.year}-${item.month}-01`).getMonth();
      
      if (!acc[year]) acc[year] = {};
      if (!acc[year][month]) acc[year][month] = [];
      
      acc[year][month].push(item);
      return acc;
    }, {});

    // Sort within each month
    Object.keys(groups).forEach(year => {
      Object.keys(groups[year]).forEach(month => {
        groups[year][month].sort((a, b) => {
          const dateA = new Date(a.publishDate || `${a.year}-${a.month}-01`);
          const dateB = new Date(b.publishDate || `${b.year}-${b.month}-01`);
          return dateB - dateA;
        });
      });
    });

    return groups;
  }, [data]);

  // Get unique years from data for navigation
  const years = useMemo(() => {
    return [...new Set(data.map(item => item.year))].sort((a, b) => b - a);
  }, [data]);

  /**
   * Cleans text content by removing markdown and formatting artifacts
   * 
   * @param {string} text - Raw text to clean
   * @returns {string} Cleaned text without formatting artifacts
   */
  const cleanText = (text) => {
    return text
      .replace(/\*\*/g, '') // Remove **
      .replace(/\"\"/g, '') // Remove ""
      .replace(/\'\'/g, '') // Remove ''
      .replace(/\s+/g, ' ') // Clean up extra spaces
      .trim();
  };

  /**
   * Opens a PDF modal for viewing meeting minutes
   * Manages modal stacking with z-index and positioning offsets
   * 
   * @param {string|number} id - Unique identifier for the meeting
   * @param {string} title - Meeting title for modal header
   * @param {string} pdfUrl - URL or filename for the PDF document
   */
  const openPDFModal = (id, title, pdfUrl) => {

    const existingModalIndex = modals.findIndex((modal) => modal.id === id);
    const maxZIndex = Math.max(...modals.map((modal) => modal.zIndex), 0) + 1;

    if (existingModalIndex !== -1) {
      const updatedModals = [...modals];
      updatedModals[existingModalIndex].isOpen = true;
      updatedModals[existingModalIndex].zIndex = maxZIndex;
      setModals(updatedModals);
    } else {
      const newModal = {
        id,
        title,
        pdfUrl,
        isOpen: true,
        top: `${15 + (modals.length * 2)}%`,
        left: `${30 + (modals.length * 2)}%`,
        zIndex: maxZIndex,
      };
      setModals((prevModals) => [...prevModals, newModal]);
    }
  };

  /**
   * Closes a PDF modal by its unique identifier
   * 
   * @param {string|number} id - Unique identifier for the modal to close
   */
  const closePDFModal = (id) => {
    setModals((prevModals) =>
      prevModals.map((modal) =>
        modal.id === id ? { ...modal, isOpen: false } : modal
      )
    );
  };

  return (
    <div>
      {/* Year Navigation */}
      <div className="minutes-navigation">
        <div className="year-selector">
          {years.map(year => (
            <button
              key={year}
              className={`year-btn ${selectedYear === year ? 'active' : ''}`}
              onClick={() => setSelectedYear(year)}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      <Tabs>
        <TabList>
          <Tab>
            <i className="fa-solid fa-table-cells"></i> Card View
          </Tab>
          <Tab>
            <i className="fa-solid fa-list"></i> List View
          </Tab>
        </TabList>

        <TabPanel>
          {/* Only show selected year's data */}
          {groupedMinutes[selectedYear] ? (
            <div className="year-section">
              <section className="minutes-grid">
                {Object.entries(groupedMinutes[selectedYear])
                  .sort(([monthA], [monthB]) => Number(monthB) - Number(monthA))
                  .flatMap(([_, items]) => items)
                  .length > 0 ? (
                  Object.entries(groupedMinutes[selectedYear])
                    .sort(([monthA], [monthB]) => Number(monthB) - Number(monthA))
                    .flatMap(([_, items]) => items)
                    .map((item) => (
                      <Tippy 
                        key={item.id}
                        content={item.summary}
                        placement="right"
                        interactive={true}
                        appendTo={() => document.body}
                        onMount={(instance) => {
                          if (instance.reference.getBoundingClientRect().right + 400 > window.innerWidth) {
                            instance.setProps({ placement: 'left' });
                          }
                        }}
                        theme="light"
                        duration={200}
                        delay={[200, 0]}
                      >
                        <div 
                          className="minute-card"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPDFModal(item.id, item.title, item.fileName);
                          }}
                        >
                          <div className="minute-card-content">
                            <h3 className="minute-title">{cleanText(item.title)}</h3>
                            <div className="minute-date">
                              {item.month} {item.year}
                            </div>
                            <p className="minute-summary">
                              {cleanText(item.summary)}
                            </p>
                            <div className="pdf-icon">
                              <i className="fa-regular fa-file-pdf"></i>
                            </div>
                          </div>
                        </div>
                      </Tippy>
                    ))
                ) : (
                  <div className="no-minutes-message">
                    <i className="fa-regular fa-folder-open"></i>
                    <p>No minutes available</p>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="no-minutes-message">
              <i className="fa-regular fa-folder-open"></i>
              <p>No minutes available</p>
            </div>
          )}
        </TabPanel>

        <TabPanel>
          <div className="minutes-list">
            {groupedMinutes[selectedYear] && 
              Object.entries(groupedMinutes[selectedYear])
                .sort(([monthA], [monthB]) => Number(monthB) - Number(monthA))
                .flatMap(([_, items]) => items)
                .length > 0 ? (
              Object.entries(groupedMinutes[selectedYear])
                .sort(([monthA], [monthB]) => Number(monthB) - Number(monthA))
                .flatMap(([_, items]) => items)
                .map((meeting) => (
                  <div key={meeting.id} className="meeting-list-item">
                    <div 
                      className="meeting-list-content"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPDFModal(meeting.id, meeting.title, meeting.fileName);
                      }}
                    >
                      <div className="meeting-list-header">
                        <h3 className="meeting-list-title">{cleanText(meeting.title)}</h3>
                        <span className="meeting-list-date">
                          <i className="fa-regular fa-calendar"></i>
                          {meeting.month} {meeting.year}
                        </span>
                      </div>
                      <p className="meeting-list-summary">{cleanText(meeting.summary)}</p>
                      <div className="pdf-icon">
                        <i className="fa-regular fa-file-pdf"></i>
                      </div>
                    </div>
                  </div>
                ))
            ) : (
              <div className="no-minutes-message">
                <i className="fa-regular fa-folder-open"></i>
                <p>No minutes available</p>
              </div>
            )}
          </div>
        </TabPanel>
      </Tabs>
      
      {modals.map((modal) => (
        <PDFModal
          key={modal.id}
          title={modal.title}
          url={modal.pdfUrl}
          show={modal.isOpen}
          onClose={(e) => {
            e.stopPropagation();
            closePDFModal(modal.id);
          }}
          allowFullscreen={true}
          allowMove={true}
          showLinkinToolBar={true}
          style={{ zIndex: modal.zIndex }}
        />
      ))}
    </div>
  );
}

export default WGMinutes;